/**
 * ReviewCaseDialog — the PR Reviews tab's drill-in (YAG-440): the SAME
 * ReviewCaseFile the Work page renders, inside a Dialog. What was a
 * read-only report modal becomes the full case: confirm/decline a held
 * draft, re-run, specialists, history — one body, two addresses.
 *
 * Identity: the dialog is keyed by `prStateId` (the row identity and the URL
 * param), because a deep link must work for rows outside the loaded page or
 * hidden by filters. It resolves the case itself: the pr-state reviews read
 * carries `pr.workItemId` (Phase 0), then the item comes from
 * GET /api/work-items/:id. A null pointer is the ghost row — a watched PR no
 * review run has touched — and renders the honest empty state with the one
 * verb that can heal it (Run review). A 404 on the item read (reader
 * visibility, deleted case) renders an error state, never a blank dialog.
 */
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import type { WorkItem } from "@yagni/shared";
import {
  Button,
  Chip,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  IconButton,
} from "../../ui/index.js";
import { usePrStateReviews } from "../../workers/useWorkers.js";
import {
  GH_STATUS_CHIP,
  GH_STATUS_TONE,
  RERUN_LABEL,
  RERUN_QUEUED,
  REVIEWS_ERROR,
  REVIEWS_LOADING,
} from "../../workers/copy.js";
import { deskAccent } from "../../desks/deskAccent.js";
import { ReviewCaseFile } from "./ReviewCaseFile.js";
import { useWorkerLookup } from "../useWorkerLookup.js";
import { useTeamIdentity } from "../useTeamIdentity.js";
import "./reviewCase.css";

/** The one case read the dialog needs; 404 is a state, not an exception. */
function useWorkItemById(workItemId: string | null): {
  item: WorkItem | null;
  missing: boolean;
  loading: boolean;
} {
  const [item, setItem] = useState<WorkItem | null>(null);
  const [missing, setMissing] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!workItemId) {
      setItem(null);
      setMissing(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`/api/work-items/${encodeURIComponent(workItemId)}`, { credentials: "include" })
      .then(async (res) => {
        if (cancelled) return;
        if (res.status === 404) {
          setItem(null);
          setMissing(true);
          return;
        }
        if (!res.ok) throw new Error(String(res.status));
        setItem((await res.json()) as WorkItem);
        setMissing(false);
      })
      .catch(() => {
        if (!cancelled) {
          setItem(null);
          setMissing(true);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [workItemId]);

  return { item, missing, loading };
}

/**
 * The fixed bar's pager: where the open row sits in the table's display
 * order. `index === -1` is a deep link to a row outside the loaded page —
 * the pager hides, the close stays.
 */
export interface ReviewCaseNav {
  index: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}

export function ReviewCaseDialog({
  prStateId,
  onClose,
  onAdvance,
  nav,
}: {
  prStateId: string | null;
  onClose: () => void;
  /**
   * Advance after a resolve — the same next-in-line the bar's right arrow
   * targets; absent (or exhausted — the caller decides) falls back to closing.
   */
  onAdvance?: () => void;
  nav?: ReviewCaseNav;
}) {
  const { data, isLoading, error, rerun } = usePrStateReviews(prStateId);
  const [rerunNote, setRerunNote] = useState<string | null>(null);
  const workItemId = data?.pr.workItemId ?? null;
  const { item, missing, loading: itemLoading } = useWorkItemById(workItemId);
  // The case header's attribution, resolved here rather than prop-drilled:
  // the same roster + directory reads the Work surface threads down.
  const workerOf = useWorkerLookup();
  const { roster } = useTeamIdentity();
  const deskName =
    (item?.deskId ? roster.find((d) => d.id === item.deskId)?.name : null) ?? "General";

  return (
    <Dialog
      open={prStateId !== null}
      onOpenChange={(open) => {
        if (!open) {
          setRerunNote(null);
          onClose();
        }
      }}
    >
      {prStateId !== null ? (
        <DialogContent
          hideClose
          className={
            // Zero padding (the bar and the case column own theirs, so the
            // bar's border and the scrollbar reach the dialog's edges). The
            // bang matters: tailwind-merge can't tell p-xl is padding (custom
            // token), so the base p-xl survives and must be out-shouted.
            "rvc-dialog max-w-5xl p-0! overflow-hidden" +
            // Fixed height only once the case renders: an empty or loading
            // dialog hugging one line is right.
            (item !== null ? " h-[min(1040px,calc(100dvh-4rem))]" : "")
          }
        >
          <div className="rvc-dialog-bar">
            {nav && nav.index >= 0 ? (
              <div className="rvc-dialog-nav" data-testid="rvc-dialog-nav">
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Previous pull request"
                  disabled={nav.index <= 0}
                  onClick={nav.onPrev}
                  data-testid="rvc-nav-prev"
                >
                  <ChevronLeft className="h-4 w-4" />
                </IconButton>
                <span className="rvc-dialog-count">
                  {nav.index + 1} / {nav.total}
                </span>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label="Next pull request"
                  disabled={nav.index >= nav.total - 1}
                  onClick={nav.onNext}
                  data-testid="rvc-nav-next"
                >
                  <ChevronRight className="h-4 w-4" />
                </IconButton>
              </div>
            ) : null}
            <DialogClose asChild>
              <IconButton variant="ghost" size="sm" aria-label="Close" className="rvc-dialog-close">
                <X className="h-4 w-4" />
              </IconButton>
            </DialogClose>
          </div>
          <div className="rvc-dialog-scroll">
            {isLoading && !data ? (
              <p className="wrk-empty rvc-dialog-pad" data-testid="rvc-dialog-loading">
                {REVIEWS_LOADING}
              </p>
            ) : error || !data ? (
              <p className="wrk-empty rvc-dialog-pad" data-testid="rvc-dialog-error">
                {REVIEWS_ERROR}
              </p>
            ) : workItemId === null ? (
              // The ghost row: watched, never reviewed, no case minted yet.
              <div className="rvc-dialog-empty rvc-dialog-pad" data-testid="rvc-dialog-ghost">
                <DialogTitle>
                  #{data.pr.prNumber} · {data.pr.title ?? data.pr.repoFullName}
                </DialogTitle>
                <div className="wrk-review-header-meta">
                  {data.pr.author ? (
                    <span className="wrk-review-author">{data.pr.author}</span>
                  ) : null}
                  <Chip tone={GH_STATUS_TONE[data.pr.githubStatus]}>
                    {GH_STATUS_CHIP[data.pr.githubStatus]}
                  </Chip>
                  <a
                    href={data.pr.prUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="wrk-review-pr-link"
                  >
                    {data.pr.repoFullName}#{data.pr.prNumber}
                  </a>
                </div>
                <p className="wrk-empty">
                  Proctor has not reviewed this pull request yet, so there is no case to open.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    void rerun().then(({ ok, message }) => {
                      setRerunNote(ok ? RERUN_QUEUED : message);
                    });
                  }}
                  data-testid="rvc-ghost-run"
                >
                  {RERUN_LABEL}
                </Button>
                {rerunNote ? (
                  <span className="rvc-rerun-note" data-testid="rvc-ghost-rerun-note">
                    {rerunNote}
                  </span>
                ) : null}
              </div>
            ) : itemLoading && !item ? (
              <p className="wrk-empty rvc-dialog-pad" data-testid="rvc-dialog-loading">
                {REVIEWS_LOADING}
              </p>
            ) : missing || !item ? (
              <p className="wrk-empty rvc-dialog-pad" data-testid="rvc-dialog-item-missing">
                This review's case is not readable here — it may belong to a Team you do not
                subscribe to, or it was removed.
              </p>
            ) : (
              <>
                {/* The Dialog needs a title for a11y; the case header carries the
                    visible one. */}
                <DialogTitle className="sr-only">
                  #{data.pr.prNumber} · {data.pr.title ?? data.pr.repoFullName}
                </DialogTitle>
                <ReviewCaseFile
                  item={item}
                  deskName={deskName}
                  accent={item.deskId ? deskAccent(item.deskId) : "var(--accent)"}
                  workerOf={workerOf}
                  onNext={onAdvance ?? onClose}
                  context="dialog"
                />
              </>
            )}
          </div>
        </DialogContent>
      ) : null}
    </Dialog>
  );
}
